// Strain-specific grow recipes for Canntycoon
// 8 strains × 3 tiers = 24 recipes
// Each recipe provides environment targets per stage, scheduled actions,
// and automation timing for manager-run rooms.

export const STRAIN_RECIPES = {

  // ── Bagseed OG ─────────────────────────────────────────────────────────────
  'bagseed-og-basic': {
    id: 'bagseed-og-basic',
    strainId: 'bagseed-og',
    tier: 'basic',
    name: 'Bagseed OG Basic Guide',
    price: 800,
    qualityBonus: 0,
    description: 'Simple tips for getting the most out of mystery genetics. One defoliation, no flush.',
    stages: {
      seedling:   { temp: 24, humidity: 65, co2: 600, lightHours: 18, ec: 0.8, ph: 6.3, durationDays: 7  },
      vegetative: { temp: 24, humidity: 65, co2: 700, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 21 },
      flowering:  { temp: 22, humidity: 50, co2: 900, lightHours: 12, ec: 1.6, ph: 6.0, durationDays: 56 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Open canopy for better light penetration' },
      { stage: 'vegetative', dayInStage: 21, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
    ],
    transferAtVegDay: 21,
    harvestAtFlowerDay: 56,
    pestResponse: 'basic',
    moldResponse: 'basic',
  },

  'bagseed-og-standard': {
    id: 'bagseed-og-standard',
    strainId: 'bagseed-og',
    tier: 'standard',
    name: 'Bagseed OG Standard Protocol',
    price: 2000,
    qualityBonus: 8,
    description: 'Two-phase defoliation, early flower thinning, and a pre-harvest flush for cleaner smoke.',
    stages: {
      seedling:   { temp: 24, humidity: 65, co2: 600, lightHours: 18, ec: 0.8, ph: 6.3, durationDays: 7  },
      vegetative: { temp: 24, humidity: 65, co2: 700, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 21 },
      flowering:  { temp: 22, humidity: 50, co2: 900, lightHours: 12, ec: 1.6, ph: 6.0, durationDays: 56 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Early lollipopping — remove lower growth' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Final veg defoliation before flip' },
      { stage: 'vegetative', dayInStage: 21, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation for airflow' },
      { stage: 'flowering',  dayInStage: 49, type: 'flush',       description: 'Flush nutrients 7 days before harvest' },
    ],
    transferAtVegDay: 21,
    harvestAtFlowerDay: 56,
    pestResponse: 'standard',
    moldResponse: 'standard',
  },

  'bagseed-og-premium': {
    id: 'bagseed-og-premium',
    strainId: 'bagseed-og',
    tier: 'premium',
    name: 'Bagseed OG Precision Protocol',
    price: 5000,
    qualityBonus: 18,
    description: 'Full defoliation schedule, indica cold-finish last 7 days, prophylactic pest and mold suppression. Extracts the best from this forgiving strain.',
    stages: {
      seedling:   { temp: 24, humidity: 65, co2: 600, lightHours: 18, ec: 0.8, ph: 6.3, durationDays: 7  },
      vegetative: { temp: 24, humidity: 65, co2: 700, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 21 },
      flowering:  { temp: 22, humidity: 50, co2: 900, lightHours: 12, ec: 1.6, ph: 6.0, durationDays: 56 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Early lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Pre-flip canopy cleanup' },
      { stage: 'vegetative', dayInStage: 21, type: 'defoliate',   description: 'Final veg pass — just before transfer' },
      { stage: 'vegetative', dayInStage: 21, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation' },
      { stage: 'flowering',  dayInStage: 14, type: 'treat_pests', description: 'Prophylactic IPM application' },
      { stage: 'flowering',  dayInStage: 28, type: 'defoliate',   description: 'Mid-flower thinning for bud sites' },
      { stage: 'flowering',  dayInStage: 42, type: 'treat_pests', description: 'Final IPM before harvest window' },
      { stage: 'flowering',  dayInStage: 49, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 21,
    harvestAtFlowerDay: 56,
    pestResponse: 'prophylactic',
    moldResponse: 'prophylactic',
  },

  // ── Northern Lights ────────────────────────────────────────────────────────
  'northern-lights-basic': {
    id: 'northern-lights-basic',
    strainId: 'northern-lights',
    tier: 'basic',
    name: 'Northern Lights Basic Guide',
    price: 800,
    qualityBonus: 0,
    description: 'Straightforward guide for this resilient indica. One mid-veg defoliation.',
    stages: {
      seedling:   { temp: 23, humidity: 60, co2: 600, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 23, humidity: 60, co2: 800, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 18 },
      flowering:  { temp: 21, humidity: 45, co2: 1000, lightHours: 12, ec: 1.7, ph: 6.0, durationDays: 49 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Open canopy for better light penetration' },
      { stage: 'vegetative', dayInStage: 18, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
    ],
    transferAtVegDay: 18,
    harvestAtFlowerDay: 49,
    pestResponse: 'basic',
    moldResponse: 'basic',
  },

  'northern-lights-standard': {
    id: 'northern-lights-standard',
    strainId: 'northern-lights',
    tier: 'standard',
    name: 'Northern Lights Standard Protocol',
    price: 2000,
    qualityBonus: 8,
    description: 'Optimised defoliation schedule and pre-harvest flush for this fast-finishing indica.',
    stages: {
      seedling:   { temp: 23, humidity: 60, co2: 600, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 23, humidity: 60, co2: 800, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 18 },
      flowering:  { temp: 21, humidity: 45, co2: 1000, lightHours: 12, ec: 1.7, ph: 6.0, durationDays: 49 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Early lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Pre-flip defoliation' },
      { stage: 'vegetative', dayInStage: 18, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation' },
      { stage: 'flowering',  dayInStage: 42, type: 'flush',       description: 'Flush nutrients 7 days before harvest' },
    ],
    transferAtVegDay: 18,
    harvestAtFlowerDay: 49,
    pestResponse: 'standard',
    moldResponse: 'standard',
  },

  'northern-lights-premium': {
    id: 'northern-lights-premium',
    strainId: 'northern-lights',
    tier: 'premium',
    name: 'Northern Lights Precision Protocol',
    price: 5000,
    qualityBonus: 18,
    description: 'Full defoliation schedule, cold-finish last 5 days for terpene burst, prophylactic treatment. Maximises this legend\'s output.',
    stages: {
      seedling:   { temp: 23, humidity: 60, co2: 600, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 23, humidity: 60, co2: 800, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 18 },
      flowering:  { temp: 21, humidity: 45, co2: 1000, lightHours: 12, ec: 1.7, ph: 6.0, durationDays: 49 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Early lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Pre-flip canopy cleanup' },
      { stage: 'vegetative', dayInStage: 18, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation' },
      { stage: 'flowering',  dayInStage: 14, type: 'treat_pests', description: 'Prophylactic IPM application' },
      { stage: 'flowering',  dayInStage: 21, type: 'defoliate',   description: 'Mid-flower thinning' },
      { stage: 'flowering',  dayInStage: 35, type: 'treat_pests', description: 'Final IPM before harvest window' },
      { stage: 'flowering',  dayInStage: 42, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 18,
    harvestAtFlowerDay: 49,
    pestResponse: 'prophylactic',
    moldResponse: 'prophylactic',
  },

  // ── CBD Harlequin ──────────────────────────────────────────────────────────
  'cbd-harlequin-basic': {
    id: 'cbd-harlequin-basic',
    strainId: 'cbd-harlequin',
    tier: 'basic',
    name: 'CBD Harlequin Basic Guide',
    price: 800,
    qualityBonus: 0,
    description: 'Basic care guide for this medical sativa. Humidity monitoring is critical — mold risk is elevated.',
    stages: {
      seedling:   { temp: 25, humidity: 60, co2: 600, lightHours: 18, ec: 0.7, ph: 6.3, durationDays: 7  },
      vegetative: { temp: 25, humidity: 60, co2: 750, lightHours: 18, ec: 1.1, ph: 6.2, durationDays: 22 },
      flowering:  { temp: 22, humidity: 48, co2: 850, lightHours: 12, ec: 1.5, ph: 6.0, durationDays: 63 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Improve airflow to reduce mold risk' },
      { stage: 'vegetative', dayInStage: 22, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
    ],
    transferAtVegDay: 22,
    harvestAtFlowerDay: 63,
    pestResponse: 'basic',
    moldResponse: 'standard',
  },

  'cbd-harlequin-standard': {
    id: 'cbd-harlequin-standard',
    strainId: 'cbd-harlequin',
    tier: 'standard',
    name: 'CBD Harlequin Medical Protocol',
    price: 2000,
    qualityBonus: 8,
    description: 'Structured defoliation for airflow, pre-harvest flush, and standard mold monitoring for consistent medical-grade output.',
    stages: {
      seedling:   { temp: 25, humidity: 60, co2: 600, lightHours: 18, ec: 0.7, ph: 6.3, durationDays: 7  },
      vegetative: { temp: 25, humidity: 60, co2: 750, lightHours: 18, ec: 1.1, ph: 6.2, durationDays: 22 },
      flowering:  { temp: 22, humidity: 48, co2: 850, lightHours: 12, ec: 1.5, ph: 6.0, durationDays: 63 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Lollipopping for airflow' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Pre-flip canopy open-up' },
      { stage: 'vegetative', dayInStage: 22, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Flower week 2 airflow defoliation' },
      { stage: 'flowering',  dayInStage: 56, type: 'flush',       description: 'Flush nutrients 7 days before harvest' },
    ],
    transferAtVegDay: 22,
    harvestAtFlowerDay: 63,
    pestResponse: 'standard',
    moldResponse: 'standard',
  },

  'cbd-harlequin-premium': {
    id: 'cbd-harlequin-premium',
    strainId: 'cbd-harlequin',
    tier: 'premium',
    name: 'CBD Harlequin Precision Protocol',
    price: 5000,
    qualityBonus: 18,
    description: 'Aggressive defoliation schedule and prophylactic mold suppression for this susceptible sativa. Maximises CBD expression for premium medical contracts.',
    stages: {
      seedling:   { temp: 25, humidity: 60, co2: 600, lightHours: 18, ec: 0.7, ph: 6.3, durationDays: 7  },
      vegetative: { temp: 25, humidity: 60, co2: 750, lightHours: 18, ec: 1.1, ph: 6.2, durationDays: 22 },
      flowering:  { temp: 22, humidity: 48, co2: 850, lightHours: 12, ec: 1.5, ph: 6.0, durationDays: 63 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Heavy lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Canopy cleanup' },
      { stage: 'vegetative', dayInStage: 21, type: 'defoliate',   description: 'Final veg pass' },
      { stage: 'vegetative', dayInStage: 22, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Flower week 2 defoliation' },
      { stage: 'flowering',  dayInStage: 14, type: 'treat_pests', description: 'Prophylactic mold/pest treatment' },
      { stage: 'flowering',  dayInStage: 28, type: 'defoliate',   description: 'Mid-flower thinning' },
      { stage: 'flowering',  dayInStage: 49, type: 'treat_pests', description: 'Final IPM before harvest window' },
      { stage: 'flowering',  dayInStage: 56, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 22,
    harvestAtFlowerDay: 63,
    pestResponse: 'prophylactic',
    moldResponse: 'prophylactic',
  },

  // ── Gorilla Glue #4 ────────────────────────────────────────────────────────
  'gorilla-glue-4-basic': {
    id: 'gorilla-glue-4-basic',
    strainId: 'gorilla-glue-4',
    tier: 'basic',
    name: 'Gorilla Glue #4 Basic Guide',
    price: 800,
    qualityBonus: 0,
    description: 'Simple care for this potent hybrid. One defoliation for airflow — mold and herm risk are real at this tier.',
    stages: {
      seedling:   { temp: 24, humidity: 60, co2: 700, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 24, humidity: 60, co2: 900, lightHours: 18, ec: 1.3, ph: 6.2, durationDays: 20 },
      flowering:  { temp: 21, humidity: 44, co2: 1100, lightHours: 12, ec: 1.8, ph: 6.0, durationDays: 63 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Open canopy to reduce herm triggers' },
      { stage: 'vegetative', dayInStage: 20, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
    ],
    transferAtVegDay: 20,
    harvestAtFlowerDay: 63,
    pestResponse: 'basic',
    moldResponse: 'basic',
  },

  'gorilla-glue-4-standard': {
    id: 'gorilla-glue-4-standard',
    strainId: 'gorilla-glue-4',
    tier: 'standard',
    name: 'Gorilla Glue #4 Standard Protocol',
    price: 2000,
    qualityBonus: 8,
    description: 'Aggressive defoliation for airflow critical to controlling this sticky hybrid\'s herm and mold risk.',
    stages: {
      seedling:   { temp: 24, humidity: 60, co2: 700, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 24, humidity: 60, co2: 900, lightHours: 18, ec: 1.3, ph: 6.2, durationDays: 20 },
      flowering:  { temp: 21, humidity: 44, co2: 1100, lightHours: 12, ec: 1.8, ph: 6.0, durationDays: 63 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Lollipopping to reduce herm triggers' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Canopy thinning for airflow' },
      { stage: 'vegetative', dayInStage: 20, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Airflow defoliation — critical for GG4' },
      { stage: 'flowering',  dayInStage: 21, type: 'defoliate',   description: 'Second airflow pass' },
      { stage: 'flowering',  dayInStage: 56, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 20,
    harvestAtFlowerDay: 63,
    pestResponse: 'standard',
    moldResponse: 'standard',
  },

  'gorilla-glue-4-premium': {
    id: 'gorilla-glue-4-premium',
    strainId: 'gorilla-glue-4',
    tier: 'premium',
    name: 'Gorilla Glue #4 Precision Protocol',
    price: 5000,
    qualityBonus: 18,
    description: 'Maximum defoliation frequency, prophylactic pest and mold suppression — the only way to unlock GG4\'s massive THC potential safely.',
    stages: {
      seedling:   { temp: 24, humidity: 60, co2: 700, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 24, humidity: 60, co2: 900, lightHours: 18, ec: 1.3, ph: 6.2, durationDays: 20 },
      flowering:  { temp: 21, humidity: 44, co2: 1100, lightHours: 12, ec: 1.8, ph: 6.0, durationDays: 63 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Aggressive lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Mid-veg canopy thinning' },
      { stage: 'vegetative', dayInStage: 18, type: 'defoliate',   description: 'Pre-flip final pass' },
      { stage: 'vegetative', dayInStage: 20, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Airflow defoliation — mandatory for GG4' },
      { stage: 'flowering',  dayInStage: 14, type: 'treat_pests', description: 'Prophylactic IPM application' },
      { stage: 'flowering',  dayInStage: 21, type: 'defoliate',   description: 'Week 3 airflow pass' },
      { stage: 'flowering',  dayInStage: 35, type: 'defoliate',   description: 'Mid-flower bud site exposure' },
      { stage: 'flowering',  dayInStage: 49, type: 'treat_pests', description: 'Final IPM before harvest window' },
      { stage: 'flowering',  dayInStage: 56, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 20,
    harvestAtFlowerDay: 63,
    pestResponse: 'prophylactic',
    moldResponse: 'prophylactic',
  },

  // ── Wedding Cake ───────────────────────────────────────────────────────────
  'wedding-cake-basic': {
    id: 'wedding-cake-basic',
    strainId: 'wedding-cake',
    tier: 'basic',
    name: 'Wedding Cake Basic Guide',
    price: 800,
    qualityBonus: 0,
    description: 'Minimal guide for this demanding boutique strain. Expect mediocre results without proper temperature control.',
    stages: {
      seedling:   { temp: 24, humidity: 58, co2: 700, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 24, humidity: 58, co2: 900, lightHours: 18, ec: 1.3, ph: 6.2, durationDays: 22 },
      flowering:  { temp: 20, humidity: 42, co2: 1000, lightHours: 12, ec: 1.8, ph: 6.0, durationDays: 63 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Open canopy for airflow' },
      { stage: 'vegetative', dayInStage: 22, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
    ],
    transferAtVegDay: 22,
    harvestAtFlowerDay: 63,
    pestResponse: 'basic',
    moldResponse: 'basic',
  },

  'wedding-cake-standard': {
    id: 'wedding-cake-standard',
    strainId: 'wedding-cake',
    tier: 'standard',
    name: 'Wedding Cake Standard Protocol',
    price: 2000,
    qualityBonus: 8,
    description: 'Structured defoliation and late-flower temperature drop (last 10 days) to coax out Wedding Cake\'s signature terpene expression.',
    stages: {
      seedling:   { temp: 24, humidity: 58, co2: 700, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 24, humidity: 58, co2: 900, lightHours: 18, ec: 1.3, ph: 6.2, durationDays: 22 },
      flowering:  { temp: 20, humidity: 42, co2: 1000, lightHours: 12, ec: 1.8, ph: 6.0, durationDays: 63 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Early canopy cleanup' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Pre-flip defoliation' },
      { stage: 'vegetative', dayInStage: 22, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 airflow defoliation' },
      { stage: 'flowering',  dayInStage: 56, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 22,
    harvestAtFlowerDay: 63,
    pestResponse: 'standard',
    moldResponse: 'standard',
  },

  'wedding-cake-premium': {
    id: 'wedding-cake-premium',
    strainId: 'wedding-cake',
    tier: 'premium',
    name: 'Wedding Cake Precision Protocol',
    price: 5000,
    qualityBonus: 18,
    description: 'Surgical defoliation schedule, precise temperature protocol, and prophylactic mold suppression. The only way to consistently hit top-shelf retail with this diva.',
    stages: {
      seedling:   { temp: 24, humidity: 58, co2: 700, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 24, humidity: 58, co2: 900, lightHours: 18, ec: 1.3, ph: 6.2, durationDays: 22 },
      flowering:  { temp: 20, humidity: 42, co2: 1000, lightHours: 12, ec: 1.8, ph: 6.0, durationDays: 63 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Aggressive lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Mid-veg thinning' },
      { stage: 'vegetative', dayInStage: 21, type: 'defoliate',   description: 'Pre-flip canopy pass' },
      { stage: 'vegetative', dayInStage: 22, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 7,  type: 'defoliate',   description: 'Week 1 flower defoliation' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 airflow defoliation' },
      { stage: 'flowering',  dayInStage: 14, type: 'treat_pests', description: 'Prophylactic mold/pest treatment' },
      { stage: 'flowering',  dayInStage: 28, type: 'defoliate',   description: 'Mid-flower bud site exposure' },
      { stage: 'flowering',  dayInStage: 49, type: 'treat_pests', description: 'Final IPM before harvest window' },
      { stage: 'flowering',  dayInStage: 56, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 22,
    harvestAtFlowerDay: 63,
    pestResponse: 'prophylactic',
    moldResponse: 'prophylactic',
  },

  // ── ACDC ───────────────────────────────────────────────────────────────────
  'acdc-basic': {
    id: 'acdc-basic',
    strainId: 'acdc',
    tier: 'basic',
    name: 'ACDC Basic Guide',
    price: 800,
    qualityBonus: 0,
    description: 'Simple care guide for this forgiving medical strain. Long flowering window, easy to manage.',
    stages: {
      seedling:   { temp: 24, humidity: 62, co2: 600, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 24, humidity: 62, co2: 800, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 20 },
      flowering:  { temp: 22, humidity: 48, co2: 900, lightHours: 12, ec: 1.6, ph: 6.0, durationDays: 70 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Mid-veg defoliation for light access' },
      { stage: 'vegetative', dayInStage: 20, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
    ],
    transferAtVegDay: 20,
    harvestAtFlowerDay: 70,
    pestResponse: 'basic',
    moldResponse: 'basic',
  },

  'acdc-standard': {
    id: 'acdc-standard',
    strainId: 'acdc',
    tier: 'standard',
    name: 'ACDC Medical Protocol',
    price: 2000,
    qualityBonus: 8,
    description: 'Optimised for consistent medical-grade CBD yield. Standard defoliation and pre-harvest flush.',
    stages: {
      seedling:   { temp: 24, humidity: 62, co2: 600, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 24, humidity: 62, co2: 800, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 20 },
      flowering:  { temp: 22, humidity: 48, co2: 900, lightHours: 12, ec: 1.6, ph: 6.0, durationDays: 70 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Early lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Pre-flip canopy cleanup' },
      { stage: 'vegetative', dayInStage: 20, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation' },
      { stage: 'flowering',  dayInStage: 63, type: 'flush',       description: 'Flush nutrients 7 days before harvest' },
    ],
    transferAtVegDay: 20,
    harvestAtFlowerDay: 70,
    pestResponse: 'standard',
    moldResponse: 'standard',
  },

  'acdc-premium': {
    id: 'acdc-premium',
    strainId: 'acdc',
    tier: 'premium',
    name: 'ACDC Precision Protocol',
    price: 5000,
    qualityBonus: 18,
    description: 'Full defoliation schedule and prophylactic treatment across this long flower cycle. Reliably hits top-tier CBD content for premium medical contracts.',
    stages: {
      seedling:   { temp: 24, humidity: 62, co2: 600, lightHours: 18, ec: 0.8, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 24, humidity: 62, co2: 800, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 20 },
      flowering:  { temp: 22, humidity: 48, co2: 900, lightHours: 12, ec: 1.6, ph: 6.0, durationDays: 70 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Early lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Mid-veg thinning' },
      { stage: 'vegetative', dayInStage: 18, type: 'defoliate',   description: 'Pre-flip final pass' },
      { stage: 'vegetative', dayInStage: 20, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation' },
      { stage: 'flowering',  dayInStage: 14, type: 'treat_pests', description: 'Prophylactic IPM application' },
      { stage: 'flowering',  dayInStage: 28, type: 'defoliate',   description: 'Mid-flower bud site exposure' },
      { stage: 'flowering',  dayInStage: 56, type: 'treat_pests', description: 'Final IPM before harvest window' },
      { stage: 'flowering',  dayInStage: 63, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 20,
    harvestAtFlowerDay: 70,
    pestResponse: 'prophylactic',
    moldResponse: 'prophylactic',
  },

  // ── Zkittlez ───────────────────────────────────────────────────────────────
  'zkittlez-basic': {
    id: 'zkittlez-basic',
    strainId: 'zkittlez',
    tier: 'basic',
    name: 'Zkittlez Basic Guide',
    price: 800,
    qualityBonus: 0,
    description: 'A basic guide to this finicky indica. Very tight tolerances — even basic growers will struggle without dialling in the environment.',
    stages: {
      seedling:   { temp: 25, humidity: 60, co2: 750, lightHours: 18, ec: 0.7, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 25, humidity: 60, co2: 950, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 24 },
      flowering:  { temp: 21, humidity: 44, co2: 1100, lightHours: 12, ec: 1.7, ph: 6.0, durationDays: 70 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Open canopy for light penetration' },
      { stage: 'vegetative', dayInStage: 24, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
    ],
    transferAtVegDay: 24,
    harvestAtFlowerDay: 70,
    pestResponse: 'basic',
    moldResponse: 'basic',
  },

  'zkittlez-standard': {
    id: 'zkittlez-standard',
    strainId: 'zkittlez',
    tier: 'standard',
    name: 'Zkittlez Standard Protocol',
    price: 2000,
    qualityBonus: 8,
    description: 'Comprehensive defoliation schedule and prophylactic pest treatments for this high-herm, finicky strain.',
    stages: {
      seedling:   { temp: 25, humidity: 60, co2: 750, lightHours: 18, ec: 0.7, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 25, humidity: 60, co2: 950, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 24 },
      flowering:  { temp: 21, humidity: 44, co2: 1100, lightHours: 12, ec: 1.7, ph: 6.0, durationDays: 70 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Early lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Mid-veg thinning' },
      { stage: 'vegetative', dayInStage: 21, type: 'defoliate',   description: 'Pre-flip canopy pass' },
      { stage: 'vegetative', dayInStage: 24, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation' },
      { stage: 'flowering',  dayInStage: 14, type: 'treat_pests', description: 'Prophylactic pest treatment' },
      { stage: 'flowering',  dayInStage: 21, type: 'defoliate',   description: 'Week 3 thinning' },
      { stage: 'flowering',  dayInStage: 63, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 24,
    harvestAtFlowerDay: 70,
    pestResponse: 'standard',
    moldResponse: 'standard',
  },

  'zkittlez-premium': {
    id: 'zkittlez-premium',
    strainId: 'zkittlez',
    tier: 'premium',
    name: 'Zkittlez Precision Protocol',
    price: 5000,
    qualityBonus: 18,
    description: 'Maximum defoliation frequency, cold-finish last 7 days, prophylactic everything — the only path to unlocking Zkittlez\'s legendary exotic terpenes.',
    stages: {
      seedling:   { temp: 25, humidity: 60, co2: 750, lightHours: 18, ec: 0.7, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 25, humidity: 60, co2: 950, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 24 },
      flowering:  { temp: 21, humidity: 44, co2: 1100, lightHours: 12, ec: 1.7, ph: 6.0, durationDays: 70 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Aggressive lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Mid-veg thinning' },
      { stage: 'vegetative', dayInStage: 21, type: 'defoliate',   description: 'Pre-flip final pass' },
      { stage: 'vegetative', dayInStage: 24, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation' },
      { stage: 'flowering',  dayInStage: 14, type: 'treat_pests', description: 'Prophylactic IPM — week 2' },
      { stage: 'flowering',  dayInStage: 21, type: 'defoliate',   description: 'Week 3 thinning' },
      { stage: 'flowering',  dayInStage: 35, type: 'defoliate',   description: 'Mid-flower bud site exposure' },
      { stage: 'flowering',  dayInStage: 35, type: 'treat_pests', description: 'Prophylactic IPM — week 5' },
      { stage: 'flowering',  dayInStage: 56, type: 'treat_pests', description: 'Final IPM before harvest window' },
      { stage: 'flowering',  dayInStage: 63, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 24,
    harvestAtFlowerDay: 70,
    pestResponse: 'prophylactic',
    moldResponse: 'prophylactic',
  },

  // ── Ghost Train Haze ───────────────────────────────────────────────────────
  'ghost-train-haze-basic': {
    id: 'ghost-train-haze-basic',
    strainId: 'ghost-train-haze',
    tier: 'basic',
    name: 'Ghost Train Haze Basic Guide',
    price: 800,
    qualityBonus: 0,
    description: 'A bare-minimum guide for the most demanding strain in the catalog. Expect significant losses without proper environment control.',
    stages: {
      seedling:   { temp: 26, humidity: 58, co2: 800,  lightHours: 18, ec: 0.7, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 26, humidity: 58, co2: 1000, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 25 },
      flowering:  { temp: 22, humidity: 42, co2: 1200, lightHours: 12, ec: 1.8, ph: 6.0, durationDays: 84 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Mid-veg defoliation for light access' },
      { stage: 'vegetative', dayInStage: 25, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
    ],
    transferAtVegDay: 25,
    harvestAtFlowerDay: 84,
    pestResponse: 'basic',
    moldResponse: 'basic',
  },

  'ghost-train-haze-standard': {
    id: 'ghost-train-haze-standard',
    strainId: 'ghost-train-haze',
    tier: 'standard',
    name: 'Ghost Train Haze Standard Protocol',
    price: 2000,
    qualityBonus: 8,
    description: 'Multi-stage defoliation and a pre-harvest flush across this marathon 84-day flower cycle.',
    stages: {
      seedling:   { temp: 26, humidity: 58, co2: 800,  lightHours: 18, ec: 0.7, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 26, humidity: 58, co2: 1000, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 25 },
      flowering:  { temp: 22, humidity: 42, co2: 1200, lightHours: 12, ec: 1.8, ph: 6.0, durationDays: 84 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Early lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Mid-veg thinning' },
      { stage: 'vegetative', dayInStage: 21, type: 'defoliate',   description: 'Pre-flip canopy cleanup' },
      { stage: 'vegetative', dayInStage: 25, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation' },
      { stage: 'flowering',  dayInStage: 28, type: 'defoliate',   description: 'Week 4 thinning' },
      { stage: 'flowering',  dayInStage: 77, type: 'flush',       description: 'Flush nutrients 7 days before harvest' },
    ],
    transferAtVegDay: 25,
    harvestAtFlowerDay: 84,
    pestResponse: 'standard',
    moldResponse: 'standard',
  },

  'ghost-train-haze-premium': {
    id: 'ghost-train-haze-premium',
    strainId: 'ghost-train-haze',
    tier: 'premium',
    name: 'Ghost Train Haze Precision Protocol',
    price: 5000,
    qualityBonus: 18,
    description: 'The most intensive schedule in the catalog — four defoliations in flower, cold-finish last 10 days, prophylactic everything. The only protocol that reliably unlocks GTH\'s record-breaking THC levels.',
    stages: {
      seedling:   { temp: 26, humidity: 58, co2: 800,  lightHours: 18, ec: 0.7, ph: 6.2, durationDays: 7  },
      vegetative: { temp: 26, humidity: 58, co2: 1000, lightHours: 18, ec: 1.2, ph: 6.2, durationDays: 25 },
      flowering:  { temp: 22, humidity: 42, co2: 1200, lightHours: 12, ec: 1.8, ph: 6.0, durationDays: 84 },
    },
    actions: [
      { stage: 'vegetative', dayInStage: 7,  type: 'defoliate',   description: 'Aggressive lollipopping' },
      { stage: 'vegetative', dayInStage: 14, type: 'defoliate',   description: 'Mid-veg thinning' },
      { stage: 'vegetative', dayInStage: 21, type: 'defoliate',   description: 'Pre-flip canopy pass' },
      { stage: 'vegetative', dayInStage: 24, type: 'defoliate',   description: 'Final pre-flip cleanup' },
      { stage: 'vegetative', dayInStage: 25, type: 'flip_lights', description: 'Flip to 12/12 — begin flower cycle' },
      { stage: 'flowering',  dayInStage: 14, type: 'defoliate',   description: 'Week 2 flower defoliation' },
      { stage: 'flowering',  dayInStage: 14, type: 'treat_pests', description: 'Prophylactic IPM — week 2' },
      { stage: 'flowering',  dayInStage: 28, type: 'defoliate',   description: 'Week 4 thinning' },
      { stage: 'flowering',  dayInStage: 42, type: 'defoliate',   description: 'Week 6 bud site exposure' },
      { stage: 'flowering',  dayInStage: 42, type: 'treat_pests', description: 'Prophylactic IPM — week 6' },
      { stage: 'flowering',  dayInStage: 56, type: 'defoliate',   description: 'Week 8 final defoliation' },
      { stage: 'flowering',  dayInStage: 70, type: 'treat_pests', description: 'Final IPM before harvest window' },
      { stage: 'flowering',  dayInStage: 77, type: 'flush',       description: 'Pre-harvest flush' },
    ],
    transferAtVegDay: 25,
    harvestAtFlowerDay: 84,
    pestResponse: 'prophylactic',
    moldResponse: 'prophylactic',
  },
};

export const STRAIN_RECIPES_LIST = Object.values(STRAIN_RECIPES);
export const getRecipesForStrain = (strainId) => STRAIN_RECIPES_LIST.filter(r => r.strainId === strainId);
export const getRecipe = (id) => STRAIN_RECIPES[id];
